import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserPlus, AlertCircle } from 'lucide-react';
const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    university: '',
    courseOfStudy: '',
    graduationYear: ''
  });
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    signup,
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    // If already authenticated, redirect to courses page
    if (isAuthenticated) {
      navigate('/courses');
    }
  }, [isAuthenticated, navigate]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatusMessage('Signing up...');
    setIsLoading(true);
    try {
      const {
        password,
        ...userData
      } = formData;
      const {
        error: signupError
      } = await signup(userData, password);
      if (signupError) {
        setError(signupError);
        setStatusMessage('');
      } else {
        setStatusMessage('Signup successful! Check your email for confirmation.');
        // Don't navigate immediately as user may need to confirm email
      }
    } catch (err) {
      setError('Failed to create an account. Please try again.');
      setStatusMessage('');
    } finally {
      setIsLoading(false);
    }
  };
  // Generate graduation year options
  const currentYear = new Date().getFullYear();
  const graduationYears = Array.from({
    length: 10
  }, (_, i) => currentYear + i);
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1180">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-1181">
        <div className="text-center" data-id="element-1182">
          <h2 className="text-3xl font-bold text-gray-800" data-id="element-1183">
            Create your account
          </h2>
          <p className="mt-2 text-gray-600" data-id="element-1184">
            Join GoABitFurther Africa Academy and start learning
          </p>
        </div>
        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" data-id="element-1185">
          {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" data-id="element-1186">
              <div className="flex" data-id="element-1187">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" data-id="element-1188" />
                <p className="text-sm text-red-700" data-id="element-1189">{error}</p>
              </div>
            </div>}
          {statusMessage && !error && <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6" data-id="element-1190">
              <p className="text-sm text-blue-700" data-id="element-1191">{statusMessage}</p>
            </div>}
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1192">
            <div data-id="element-1193">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-1194">
                Full Name
              </label>
              <div className="mt-1" data-id="element-1195">
                <input id="name" name="name" type="text" autoComplete="name" required value={formData.name} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1196" />
              </div>
            </div>
            <div data-id="element-1197">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-1198">
                Email address
              </label>
              <div className="mt-1" data-id="element-1199">
                <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1200" />
              </div>
            </div>
            <div data-id="element-1201">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-1202">
                Password
              </label>
              <div className="mt-1" data-id="element-1203">
                <input id="password" name="password" type="password" autoComplete="new-password" required value={formData.password} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1204" />
              </div>
            </div>
            <div data-id="element-1205">
              <label htmlFor="university" className="block text-sm font-medium text-gray-700" data-id="element-1206">
                University
              </label>
              <div className="mt-1" data-id="element-1207">
                <input id="university" name="university" type="text" value={formData.university} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1208" />
              </div>
            </div>
            <div data-id="element-1209">
              <label htmlFor="courseOfStudy" className="block text-sm font-medium text-gray-700" data-id="element-1210">
                Course of Study
              </label>
              <div className="mt-1" data-id="element-1211">
                <input id="courseOfStudy" name="courseOfStudy" type="text" value={formData.courseOfStudy} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1212" />
              </div>
            </div>
            <div data-id="element-1213">
              <label htmlFor="graduationYear" className="block text-sm font-medium text-gray-700" data-id="element-1214">
                Expected Graduation Year
              </label>
              <div className="mt-1" data-id="element-1215">
                <select id="graduationYear" name="graduationYear" value={formData.graduationYear} onChange={handleChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1216">
                  <option value="" data-id="element-1217">Select Year</option>
                  {graduationYears.map(year => <option key={year} value={year.toString()} data-id="element-1218">
                      {year}
                    </option>)}
                </select>
              </div>
            </div>
            <div data-id="element-1219">
              <button type="submit" disabled={isLoading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300" data-id="element-1220">
                {isLoading ? <span data-id="element-1221">Creating account...</span> : <>
                    <UserPlus className="h-5 w-5 mr-2" data-id="element-1222" />
                    Sign up
                  </>}
              </button>
            </div>
          </form>
          <div className="mt-6" data-id="element-1223">
            <div className="relative" data-id="element-1224">
              <div className="absolute inset-0 flex items-center" data-id="element-1225">
                <div className="w-full border-t border-gray-300" data-id="element-1226" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-1227">
                <span className="px-2 bg-white text-gray-500" data-id="element-1228">Or</span>
              </div>
            </div>
            <div className="mt-6 text-center" data-id="element-1229">
              <p className="text-sm text-gray-600" data-id="element-1230">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-1231">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
          <div className="mt-6 text-center" data-id="element-1232">
            <p id="status" className="text-xs text-gray-500" data-id="element-1233">
              {statusMessage || 'Status: Ready to sign up'}
            </p>
          </div>
        </div>
      </div>
    </div>;
};
export default Signup;